import { t } from "@lingui/macro";
import {
  Check,
  X,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Target,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import { AnalyzeResumeResult } from "@reactive-resume/schema";

type MandatoryRequirementsChecklistProps = {
  analysesData: AnalyzeResumeResult;
  className?: string;
};

export const MandatoryRequirementsChecklist = ({
  analysesData,
  className,
}: MandatoryRequirementsChecklistProps) => {
  if (!analysesData.metrics.mrc || !analysesData.metrics.mrc.object) {
    return null;
  }

  const { isDarkMode } = useTheme();

  // State for show more/less functionality
  const [showAllMissing, setShowAllMissing] = useState(false);
  const [showAllMet, setShowAllMet] = useState(false);

  // Number of items to show initially
  const INITIAL_ITEMS_COUNT = 4;

  const { jd_mandatory_requirements, resume_matching_requirements } =
    analysesData.metrics.mrc.object;

  // Create a set for quick lookup of matching requirements
  const matchingRequirementsSet = new Set(
    resume_matching_requirements.map((match) => match.requirement),
  );

  const matchedCount = resume_matching_requirements.length;
  const totalCount = jd_mandatory_requirements.length;
  const missingCount = totalCount - matchedCount;

  // Get match percentage from analysis results - same calculation as metric-score-card.tsx
  const score =
    analysesData.metrics.mrc.weight > 0
      ? analysesData.metrics.mrc.weightedScore / analysesData.metrics.mrc.weight
      : 0;
  const matchPercentage = Math.round(score * 100);

  const formatRequirement = (req: string) => {
    return req
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Split requirements into matched and missing
  const matchedRequirements = jd_mandatory_requirements.filter((req) =>
    matchingRequirementsSet.has(req.requirement),
  );
  const missingRequirements = jd_mandatory_requirements.filter(
    (req) => !matchingRequirementsSet.has(req.requirement),
  );

  // Get items to display based on show more state
  const displayedMissingRequirements = showAllMissing
    ? missingRequirements
    : missingRequirements.slice(0, INITIAL_ITEMS_COUNT);
  const displayedMetRequirements = showAllMet
    ? matchedRequirements
    : matchedRequirements.slice(0, INITIAL_ITEMS_COUNT);

  // Define colors - unified with metric-score-card.tsx
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#ef5350" : "#e57373",
    amber: "#ffb74d",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 183, 77, 0.15)" : "rgba(255, 183, 77, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on match percentage - unified with metric-score-card.tsx
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (matchPercentage >= 80) status = "excellent";
  else if (matchPercentage >= 60) status = "good";

  // Get status info - unified with metric-score-card.tsx
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`Your resume meets most of the mandatory requirements for this job.`,
          action: t`Keep doing what you're doing - this is a strength!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`Your resume meets many of the mandatory requirements, but there's room for improvement.`,
          action: t`Review the suggestions below to make this even stronger.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`Your resume is missing several mandatory requirements for this position.`,
          action: t`Focus on the recommendations below to improve your chances.`,
          bgColor: colors.redBg,
          textColor: colors.red,
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.textColor}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <Target className="h-5 w-5" style={{ color: statusInfo.textColor }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.textColor }}>
              {statusInfo.text} ({matchPercentage}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
            {statusInfo.description}
          </p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.textColor }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Missing Requirements - Show these first as they need action */}
        {missingRequirements.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                {t`Requirements You're Missing`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`These are mandatory requirements from the job posting that don't appear in your resume. Add these to improve your chances.`}
              </p>
            </div>
            <div className="space-y-2">
              {displayedMissingRequirements.map((requirement, index) => (
                <div
                  key={requirement.requirement}
                  className="flex items-center gap-x-3 rounded-lg p-3 transition-colors"
                  style={{ backgroundColor: colors.redBg }}
                >
                  <X className="h-4 w-4 flex-shrink-0" style={{ color: colors.red }} />
                  <div className="flex-1">
                    <span className="text-sm font-medium">
                      {formatRequirement(requirement.requirement)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Show More/Less Button for Missing Requirements */}
            {missingRequirements.length > INITIAL_ITEMS_COUNT && (
              <button
                onClick={() => setShowAllMissing(!showAllMissing)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllMissing ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${missingRequirements.length - INITIAL_ITEMS_COUNT} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Met Requirements - Show what they're doing well */}
        {matchedRequirements.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                {t`Requirements You Meet`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Great! Your resume already shows these mandatory requirements. Make sure they're prominently featured.`}
              </p>
            </div>
            <div className="space-y-2">
              {displayedMetRequirements.map((requirement, index) => (
                <div
                  key={requirement.requirement}
                  className="flex items-center gap-x-3 rounded-lg p-3 transition-colors"
                  style={{ backgroundColor: colors.greenBg }}
                >
                  <Check className="h-4 w-4 flex-shrink-0" style={{ color: colors.green }} />
                  <span className="text-sm font-medium">
                    {formatRequirement(requirement.requirement)}
                  </span>
                </div>
              ))}
            </div>

            {/* Show More/Less Button for Met Requirements */}
            {matchedRequirements.length > INITIAL_ITEMS_COUNT && (
              <button
                onClick={() => setShowAllMet(!showAllMet)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllMet ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${matchedRequirements.length - INITIAL_ITEMS_COUNT} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
